const SERIF_STACK =
  `'Playfair Display', 'Iowan Old Style', 'Palatino Linotype', Georgia, serif`;
const SANS_STACK =
  `'DM Sans', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;
const MONO_STACK =
  `ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace`;

export const widgetStyles = `
:host {
  all: initial;
  font-family: ${SANS_STACK};
}

* {
  box-sizing: border-box;
}

.mw-root {
  position: fixed;
  z-index: 2147483646;
  font-family: ${SANS_STACK};
  color: var(--mw-fg);
  --mw-radius: 14px;
}

.mw-root.mw-bottom-right { right: 20px; bottom: 20px; }
.mw-root.mw-bottom-left  { left: 20px;  bottom: 20px; }
.mw-root.mw-top-right    { right: 20px; top: 20px; }
.mw-root.mw-top-left     { left: 20px;  top: 20px; }

.mw-root.mw-dark {
  --mw-bg: #0f0a0c;
  --mw-card: #1a1214;
  --mw-fg: #f6f1e6;
  --mw-muted: #948a83;
  --mw-border: rgba(246, 241, 230, 0.10);
  --mw-input-bg: #0f0a0c;
  --mw-user-bubble: rgba(0, 212, 170, 0.10);
  --mw-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6);
}
.mw-root.mw-light {
  --mw-bg: #fafaf7;
  --mw-card: #ffffff;
  --mw-fg: #1a1214;
  --mw-muted: #6b6660;
  --mw-border: rgba(26, 18, 20, 0.10);
  --mw-input-bg: #fafaf7;
  --mw-user-bubble: rgba(0, 212, 170, 0.10);
  --mw-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.18);
}

/* Bubble */
.mw-bubble {
  width: 60px;
  height: 60px;
  border-radius: 9999px;
  background: var(--mw-accent);
  color: #0f0a0c;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3), 0 0 0 4px color-mix(in srgb, var(--mw-accent) 18%, transparent);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  outline: none;
}
.mw-bubble:hover { transform: translateY(-2px) scale(1.04); }
.mw-bubble:focus-visible { box-shadow: 0 0 0 4px color-mix(in srgb, var(--mw-accent) 50%, transparent); }
.mw-bubble svg { width: 26px; height: 26px; }

/* Panel */
.mw-panel {
  position: absolute;
  width: 380px;
  height: 580px;
  max-height: calc(100vh - 40px);
  background: var(--mw-bg);
  color: var(--mw-fg);
  border-radius: var(--mw-radius);
  border: 1px solid var(--mw-border);
  box-shadow: var(--mw-shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform-origin: bottom right;
}
.mw-bottom-right .mw-panel { right: 0; bottom: 76px; transform-origin: bottom right; }
.mw-bottom-left  .mw-panel { left:  0; bottom: 76px; transform-origin: bottom left; }
.mw-top-right    .mw-panel { right: 0; top: 76px;    transform-origin: top right; }
.mw-top-left     .mw-panel { left:  0; top: 76px;    transform-origin: top left; }

@media (max-width: 520px) {
  .mw-root { left: 0 !important; right: 0 !important; bottom: 0 !important; top: auto !important; }
  .mw-bubble { position: absolute; bottom: 16px; right: 16px; }
  .mw-panel {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    border: none;
  }
}

/* Header */
.mw-header {
  padding: 14px 16px;
  border-bottom: 1px solid var(--mw-border);
  background: var(--mw-card);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
}
.mw-header-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.mw-dot {
  width: 8px; height: 8px; border-radius: 9999px; background: var(--mw-accent);
  box-shadow: 0 0 0 0 color-mix(in srgb, var(--mw-accent) 50%, transparent);
  animation: mw-pulse 2s ease-out infinite;
}
@keyframes mw-pulse {
  0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--mw-accent) 50%, transparent); }
  70% { box-shadow: 0 0 0 8px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
}
.mw-title {
  font-family: ${SERIF_STACK};
  font-size: 14px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--mw-fg);
}
.mw-close {
  background: transparent;
  border: none;
  color: var(--mw-muted);
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mw-close:hover { color: var(--mw-fg); background: var(--mw-border); }

/* Messages */
.mw-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-anchor: none;
}
.mw-empty {
  margin: auto;
  text-align: center;
  max-width: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.mw-empty-mark {
  width: 44px; height: 44px; border-radius: 9999px;
  border: 1px solid color-mix(in srgb, var(--mw-accent) 40%, transparent);
  color: var(--mw-accent);
  font-family: ${SERIF_STACK};
  font-style: italic;
  font-size: 22px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 6px;
}
.mw-empty h3 {
  font-family: ${SERIF_STACK};
  font-weight: 500;
  font-size: 17px;
  margin: 0;
  color: var(--mw-fg);
}
.mw-empty p {
  margin: 0;
  font-size: 13px;
  color: var(--mw-muted);
  line-height: 1.5;
}

.mw-msg { display: flex; flex-direction: column; gap: 8px; }
.mw-msg-user { align-items: flex-end; }
.mw-msg-assistant { align-items: flex-start; }

.mw-bubble-text {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.mw-msg-user .mw-bubble-text {
  background: var(--mw-user-bubble);
  border: 1px solid color-mix(in srgb, var(--mw-accent) 25%, transparent);
  color: var(--mw-fg);
}
.mw-msg-assistant .mw-bubble-text {
  background: transparent;
  color: var(--mw-fg);
  font-family: ${SERIF_STACK};
  font-style: italic;
  padding-left: 0;
  padding-right: 0;
  font-size: 15px;
}
.mw-msg-assistant.mw-error .mw-bubble-text {
  font-style: normal;
  font-family: ${SANS_STACK};
  font-size: 13px;
  background: rgba(220, 80, 80, 0.10);
  border: 1px solid rgba(220, 80, 80, 0.30);
  color: #ff8a8a;
  border-radius: 8px;
  padding: 10px 12px;
}

.mw-loading {
  display: flex; align-items: center; gap: 8px;
  font-family: ${SERIF_STACK};
  font-style: italic;
  font-size: 13px;
  color: var(--mw-muted);
}
.mw-spinner {
  width: 14px; height: 14px;
  border: 2px solid color-mix(in srgb, var(--mw-accent) 30%, transparent);
  border-top-color: var(--mw-accent);
  border-radius: 9999px;
  animation: mw-spin 0.9s linear infinite;
}
@keyframes mw-spin { to { transform: rotate(360deg); } }

/* Wine card */
.mw-card-wrap { width: 100%; padding-top: 4px; }
.mw-card {
  position: relative;
  border-radius: 6px;
  background: var(--mw-card);
  border: 1px solid color-mix(in srgb, var(--mw-accent) 25%, var(--mw-border));
  padding: 18px 16px 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
  overflow: hidden;
}
.mw-card::before, .mw-card::after,
.mw-card .mw-corner {
  content: '';
  position: absolute;
  width: 8px; height: 8px;
  border-color: color-mix(in srgb, var(--mw-accent) 45%, transparent);
}
.mw-card::before { top: 6px; left: 6px;  border-top: 1px solid; border-left: 1px solid; }
.mw-card::after  { top: 6px; right: 6px; border-top: 1px solid; border-right: 1px solid; }
.mw-card .mw-corner.bl { bottom: 6px; left: 6px;  border-bottom: 1px solid; border-left: 1px solid; }
.mw-card .mw-corner.br { bottom: 6px; right: 6px; border-bottom: 1px solid; border-right: 1px solid; }

.mw-card-header { text-align: center; padding-bottom: 12px; border-bottom: 1px solid var(--mw-border); margin-bottom: 12px; }
.mw-card-region { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--mw-accent); font-weight: 500; margin: 0 0 4px; }
.mw-card-name { font-family: ${SERIF_STACK}; font-size: 22px; font-weight: 500; margin: 0; line-height: 1.2; color: var(--mw-fg); }
.mw-card-producer { font-size: 12px; color: var(--mw-muted); margin: 4px 0 0; }
.mw-card-vintage { font-family: ${SERIF_STACK}; font-style: italic; color: var(--mw-muted); margin: 4px 0 0; font-size: 14px; }

.mw-card-section { margin-bottom: 12px; font-size: 13px; color: var(--mw-fg); line-height: 1.55; }
.mw-card-notes {
  background: color-mix(in srgb, var(--mw-bg) 80%, transparent);
  border: 1px solid var(--mw-border);
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 12px;
}
.mw-card-notes-label { font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: var(--mw-muted); margin: 0 0 4px; }
.mw-card-notes-body { font-size: 12px; color: var(--mw-fg); margin: 0; line-height: 1.5; font-weight: 500; }

.mw-card-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
  margin-top: 8px;
}
.mw-stat-label { font-size: 9px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--mw-muted); margin: 0 0 2px; }
.mw-stat-value { font-family: ${SERIF_STACK}; font-size: 16px; color: var(--mw-accent); margin: 0; }
.mw-stat-value.mw-mono { font-family: ${MONO_STACK}; font-size: 13px; color: var(--mw-fg); }
.mw-signal {
  display: inline-block; padding: 3px 8px; border-radius: 999px;
  font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; font-weight: 600;
  background: color-mix(in srgb, var(--mw-accent) 18%, transparent);
  color: var(--mw-accent);
  border: 1px solid color-mix(in srgb, var(--mw-accent) 40%, transparent);
}

/* Input */
.mw-input-row {
  border-top: 1px solid var(--mw-border);
  background: var(--mw-card);
  padding: 12px;
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}
.mw-input {
  flex: 1;
  background: var(--mw-input-bg);
  border: 1px solid var(--mw-border);
  color: var(--mw-fg);
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s ease;
  min-width: 0;
}
.mw-input:focus { border-color: var(--mw-accent); }
.mw-input::placeholder { color: var(--mw-muted); }
.mw-send {
  background: var(--mw-accent);
  color: #0f0a0c;
  border: none;
  width: 38px; height: 38px;
  border-radius: 8px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.mw-send:hover:not(:disabled) { transform: translateY(-1px); }
.mw-send:disabled { opacity: 0.4; cursor: not-allowed; }
.mw-send svg { width: 18px; height: 18px; }

/* Suggestions */
.mw-suggestions {
  display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; margin-top: 10px;
}
.mw-suggestion {
  background: transparent;
  border: 1px solid var(--mw-border);
  color: var(--mw-muted);
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 11px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;
}
.mw-suggestion:hover {
  border-color: color-mix(in srgb, var(--mw-accent) 50%, transparent);
  color: var(--mw-fg);
  background: color-mix(in srgb, var(--mw-accent) 8%, transparent);
}

/* Scrollbar */
.mw-messages::-webkit-scrollbar { width: 6px; }
.mw-messages::-webkit-scrollbar-thumb { background: var(--mw-border); border-radius: 3px; }
.mw-messages::-webkit-scrollbar-track { background: transparent; }
`;
